/**
 * Event Schemas for SmarterElements
 *
 * This module defines the event types and payload schemas.
 * It provides validation functions to ensure events conform to their expected structure.
 */

type SchemaType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'date' | 'id';

interface SchemaField {
  type: SchemaType;
  required?: boolean;
  description?: string;
  fields?: Record<string, SchemaField>;
  itemType?: SchemaType | SchemaField;
}

interface SchemaDefinition extends SchemaField {
  description: string;
  example: Record<string, unknown>;
  fields: Record<string, SchemaField>;
}

// ValidationResult interface is now exported above

interface EventSchema {
  [key: string]: SchemaDefinition;
}

/**
 * Validate that a value matches a specific type
 */
function validateType(
  value: unknown,
  type: SchemaType,
  required = true
): boolean {
  // Handle undefined/null for optional fields
  if (value === undefined || value === null) {
    return !required;
  }

  switch (type) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number' && !isNaN(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'object':
      return typeof value === 'object' && !Array.isArray(value);
    case 'array':
      return Array.isArray(value);
    case 'date':
      return value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)));
    case 'id':
      return typeof value === 'string' && value.trim().length > 0;
    default:
      return false;
  }
}

/**
 * Validate an event payload against a schema
 */
function validatePayload(
  payload: Record<string, unknown>,
  schema: SchemaDefinition
): ValidationResult {
  const errors: string[] = [];
  const result: ValidationResult = { valid: true, errors };

  // Check required fields
  Object.entries(schema.fields).forEach(([field, fieldDef]) => {
    const value = payload[field];
    const isRequired = fieldDef.required !== false; // Default to required if not specified
    
    if (isRequired && (value === undefined || value === null)) {
      errors.push(`Missing required field: ${field}`);
      result.valid = false;
      return;
    }

    if (value === undefined || value === null) {
      return; // Skip further validation for undefined/null optional fields
    }

    // Validate the type
    if (fieldDef.type === 'object' && fieldDef.fields) {
      // Nested object validation
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        errors.push(`Field '${field}' must be an object`);
        result.valid = false;
      } else {
        const nestedSchema: SchemaDefinition = {
        ...fieldDef,
        fields: fieldDef.fields,
        type: 'object',
        description: fieldDef.description || '',
        example: {} // Add a default empty example
      };
      const nestedResult = validatePayload(value as Record<string, unknown>, nestedSchema);
        if (!nestedResult.valid && nestedResult.errors) {
          errors.push(...nestedResult.errors.map(e => `${field}.${e}`));
          result.valid = false;
        }
      }
    } else if (fieldDef.type === 'array' && fieldDef.itemType) {
      // Array validation
      if (!Array.isArray(value)) {
        errors.push(`Field '${field}' must be an array`);
        result.valid = false;
      } else if (typeof fieldDef.itemType === 'string') {
        // Simple array of primitives
        value.forEach((item, index) => {
          if (!validateType(item, fieldDef.itemType as SchemaType, true)) {
            errors.push(`Item at index ${index} in '${field}' array has invalid type`);
            result.valid = false;
          }
        });
      } else {
        // Array of objects
        value.forEach((item, index) => {
          if (typeof item !== 'object' || item === null) {
            errors.push(`Item at index ${index} in '${field}' must be an object`);
            result.valid = false;
          } else {
            const itemResult = validatePayload(item as Record<string, unknown>, {
              ...fieldDef.itemType as SchemaDefinition,
              fields: (fieldDef.itemType as SchemaField).fields || {}
            });
            if (!itemResult.valid && itemResult.errors) {
              errors.push(...itemResult.errors.map(e => `${field}[${index}].${e}`));
              result.valid = false;
            }
          }
        });
      }
    } else {
      // Simple type validation
      if (!validateType(value, fieldDef.type, isRequired)) {
        errors.push(`Field '${field}' must be of type ${fieldDef.type}`);
        result.valid = false;
      }
    }
  });

  return result;
}

// Export types
export type { SchemaType, SchemaField, SchemaDefinition, EventSchema };

// Export the ValidationResult interface
export interface ValidationResult {
  valid: boolean;
  errors?: string[];
}

// ============================================================================
// Event Schemas
// ============================================================================

const eventSchemas: EventSchema = {
  'exam-selected': {
    type: 'object',
    description: 'Fired when an exam is selected in the UI',
    example: {
      examId: 'exam-123',
      examName: 'Midterm Exam',
      courseId: 'course-456',
      timestamp: new Date().toISOString(),
    },
    fields: {
      examId: { type: 'id', description: 'Unique identifier for the exam' },
      examName: { type: 'string', description: 'Display name of the exam' },
      courseId: { type: 'id', description: 'Unique identifier for the course' },
      timestamp: { type: 'date', description: 'When the selection occurred' },
    },
  },
  // Add more event schemas as needed
};

/**
 * Validate an event payload against its schema
 */
function validateElementEvent(
  eventName: string,
  payload: Record<string, unknown>
): ValidationResult {
  const schema = eventSchemas[eventName];
  
  if (!schema) {
    return {
      valid: false,
      errors: [`No schema found for event: ${eventName}`],
    };
  }

  return validatePayload(payload, schema);
}

/**
 * Get documentation for all events or a specific event
 */
export function getEventDocumentation(eventName?: string): unknown {
  if (eventName) {
    const schema = eventSchemas[eventName];
    if (!schema) {
      throw new Error(`No schema found for event: ${eventName}`);
    }
    return {
      [eventName]: {
        description: schema.description,
        example: schema.example,
        fields: schema.fields,
      },
    };
  }

  // Return documentation for all events
  return Object.entries(eventSchemas).reduce((acc, [name, schema]) => {
    acc[name] = {
      description: schema.description,
      example: schema.example,
      fields: schema.fields,
    };
    return acc;
  }, {} as Record<string, unknown>);
}

// Export the schemas for documentation purposes
export { eventSchemas };

export default {
  validateElementEvent,
  getEventDocumentation,
  schemas: eventSchemas,
};
